import { PayloadAction } from '../../../store.model';
import { AxisDataAxisPath } from '../../chart/axis-data-axis-path';
import { AxisDataAxisPathPoint } from '../../chart/axis-data-axis-path-point';
import { AxisLeftTick } from '../../chart/axis-left-tick';
import { AxisRightTick } from '../../chart/axis-right-tick';
import { ImprovementGroupDto, PerformerHistoryDto } from '../../core';
import {
    TrendAxisDataAxisPathPoint,
    TrendDialogData,
    TrendDialogMode,
} from '../../trend-dialog';
import { NO_SCORE_NO_CLASS_ID } from '../fixture/no-score-no-class-id';
import { ActionType } from '../main.action';
import { MainState } from '../main.state';

import {
    HideTrendDialog,
    ShowTrendDialog,
    ShowTrendDialogResult,
} from './show-trend-dialog.action';

export function showTrendDialogReducer(
    state: MainState,
    _: PayloadAction<ActionType.MI2_MAIN__SHOW_TREND_DIALOG, ShowTrendDialog>,
): MainState {
    return {
        ...state,
        pending: {
            ...state.pending,
            showTrend: true,
        },
    };
}

function chartImprovement(dto: PerformerHistoryDto): {
    leftTicks: AxisLeftTick[];
    rightTicks: AxisRightTick[];
    points: AxisDataAxisPathPoint[];
} {
    const cats = dto?.improvementCategories
        ?.filter(
            (e) => e.scorecardImprovementCategoryId !== NO_SCORE_NO_CLASS_ID,
        )
        .sort((a, b) => b.axisOrder - a.axisOrder);
    const groups = cats?.reduce<ImprovementGroupDto[]>((p, c) => {
        c.groups?.sort((a, b) => b.axisOrder - a.axisOrder);
        p.push(...c.groups);
        return p;
    }, []);

    const leftTicks: AxisLeftTick[] = [];
    const grpTickIdLookup = {};
    let tickCount = 1;
    groups?.forEach((grp) => {
        if (grpTickIdLookup[grp.scorecardImprovementCategoryId] == null) {
            grpTickIdLookup[grp.scorecardImprovementCategoryId] = tickCount;
        }
        leftTicks.push({
            tickId: tickCount++,
            color: grp.colour,
            label: grp.scorecardImprovementGroupName,
        });
    });
    leftTicks.splice(0, 0, {
        tickId: leftTicks[0].tickId - 1,
        label: 'Worst',
        isExtentTick: true,
    });
    leftTicks.push({ tickId: tickCount, label: 'Best', isExtentTick: true });

    const rightTicks: AxisRightTick[] = [];
    tickCount = 1;
    cats?.forEach((cat, i) => {
        const tickId = grpTickIdLookup[cat.scorecardImprovementCategoryId];
        const len = cat.groups?.length ?? 1;
        const rangeStart = tickId;
        // eslint-disable-next-line @typescript-eslint/restrict-plus-operands
        const rangeEnd = tickId + (len - 1);
        rightTicks.push({
            tickId: tickCount++,
            label: cat.scorecardImprovementCategoryName,
            color: i % 2 === 0 ? '#808080' : 'transparent',
            leftAxisTickRangeStart: rangeStart - 0.5,
            // eslint-disable-next-line @typescript-eslint/restrict-plus-operands
            leftAxisTickRangeCenter: tickId + (rangeEnd - rangeStart) / 2,
            // eslint-disable-next-line @typescript-eslint/restrict-plus-operands
            leftAxisTickRangeEnd: rangeEnd + 0.5,
        });
    });

    const points = dto?.historyItems
        ?.map<TrendAxisDataAxisPathPoint>((e) => {
            return {
                x: new Date(e.periodDate),
                y: e.improvementScoreGroupId,
                color: groups?.find(
                    (ee) =>
                        ee.scorecardImprovementGroupId ===
                        e.improvementScoreGroupId,
                )?.colour,
                label: groups?.find(
                    (ee) =>
                        ee.scorecardImprovementGroupId ===
                        e.improvementScoreGroupId,
                )?.scorecardImprovementGroupName,
                improvementScore: e.improvementScore / 100,
            };
        })
        .filter((e) => e.y != null && e.y !== 0);

    return { leftTicks, rightTicks, points };
}

function chartScorecard(dto: PerformerHistoryDto): {
    leftTicks: AxisLeftTick[];
    rightTicks: AxisRightTick[];
    points: AxisDataAxisPathPoint[];
} {
    const leftTicks: AxisLeftTick[] = [];
    let tickCount = 1;
    dto?.performanceClasses?.forEach((cls) => {
        leftTicks.push({
            tickId: tickCount++,
            color: cls.colour,
            label: cls.scorecardPerformanceClassName,
        });
    });
    leftTicks.splice(0, 0, {
        tickId: leftTicks[0].tickId - 1,
        isExtentTick: true,
    });
    leftTicks.push({ tickId: tickCount, isExtentTick: true });

    const points = dto?.historyItems
        ?.map<TrendAxisDataAxisPathPoint>((e) => {
            return {
                x: new Date(e.periodDate),
                y: e.scorecardPerformanceClassId,
                color: dto?.performanceClasses?.find(
                    (ee) =>
                        ee.scorecardPerformanceClassId ===
                        e.scorecardPerformanceClassId,
                )?.colour,
                label: dto?.performanceClasses?.find(
                    (ee) =>
                        ee.scorecardPerformanceClassId ===
                        e.scorecardPerformanceClassId,
                )?.scorecardPerformanceClassName,
                scorecardPoints: e.scorecardPoints,
                superGroupName: e.superGroupName,
            };
        })
        .filter((e) => e.y != null && e.y !== 0);

    return { leftTicks, rightTicks: null, points };
}

export function showTrendDialogResultReducer(
    state: MainState,
    action: PayloadAction<
        ActionType.MI2_MAIN__SHOW_TREND_DIALOG_RESULT,
        ShowTrendDialogResult
    >,
): MainState {
    try {
        const { leftTicks, rightTicks, points } =
            action.payload.mode === TrendDialogMode.IMPROVEMENT
                ? chartImprovement(action.payload.data)
                : chartScorecard(action.payload.data);

        const paths: AxisDataAxisPath[] = [
            {
                key: 'default',
                points,
            },
        ];

        const data: TrendDialogData = {
            mode: action.payload.mode,
            showYAxisLabel2: false,
            leftTicks,
            rightTicks,
            paths,
        };

        return {
            ...state,
            pending: {
                ...state.pending,
                showTrend: false,
            },
            trendDialogData: data,
        };
    } catch {
        return {
            ...state,
        };
    }
}

export function hideTrendDialogReducer(
    state: MainState,
    _: PayloadAction<ActionType.MI2_MAIN__HIDE_TREND_DIALOG, HideTrendDialog>,
): MainState {
    return {
        ...state,
        trendDialogData: undefined,
    };
}
